import {AnimatePresence, m} from 'framer-motion'
import _ from 'lodash'
import {FC, useLayoutEffect, useState} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {useMobile} from '@/components/context/MobileProvider'
import {CP_DESKTOP_SIDEBAR_ID, CP_MOBILE_MENU_ID} from '@/components/user-portal/utils/constants'
import {HOVER_TRANSITION, SNACKBAR_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {CheckCircleIcon} from '../icons/CheckCircleIcon'
import {SnackbarType, SnackbarTypeToBackgroundColor} from './constants'
import {SnackbarProps} from './context/SnackbarContext'

export const Snackbar: FC<SnackbarProps> = ({text, type, isVisible, buttonText, onClick}) => {
  const router = useNavigation()
  const isMobile = useMobile()
  const [desktopSidebarWidth, setDesktopSidebarWidth] = useState(0)
  const [mobileMenuHeight, setMobileMenuHeight] = useState(0)

  useLayoutEffect(() => {
    const desktopSidebarEl = document.getElementById(CP_DESKTOP_SIDEBAR_ID)
    const mobileMenuEl = document.getElementById(CP_MOBILE_MENU_ID)

    setDesktopSidebarWidth(desktopSidebarEl ? desktopSidebarEl.offsetWidth + 16 : 0)
    setMobileMenuHeight(mobileMenuEl ? mobileMenuEl.offsetHeight + 12 : 0)
  }, [router.pathname])

  const backgroundColor = SnackbarTypeToBackgroundColor[type]
  const icon =
    type === SnackbarType.success ? (
      <CheckCircleIcon
        backgroundColor={theme.palette.background.default}
        checkColor={theme.palette.green[500]}
      />
    ) : undefined

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <div className="snackbar">
            <m.div
              initial={{y: 0}}
              animate={{y: isMobile ? -24 : -32}}
              exit={{opacity: 0}}
              transition={{
                opacity: {duration: SNACKBAR_TRANSITION, ease: [0.25, 0.25, 0.75, 0.75]},
                y: {
                  type: 'spring',
                  stiffness: 600,
                  damping: 15,
                  mass: 1,
                  duration: SNACKBAR_TRANSITION,
                },
              }}
              onUpdate={_.noop}
            >
              <div className="content">
                {icon && <div className="icon">{icon}</div>}
                <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                  {text}
                </Text>
                {buttonText && onClick && (
                  <button type="button" className="button-wrapper" onClick={onClick}>
                    <Text
                      variant={TextVariant.text14}
                      fontWeight={TextFontWeight.bold}
                      color={theme.palette.text.white}
                    >
                      {buttonText}
                    </Text>
                  </button>
                )}
              </div>
            </m.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .snackbar {
          width: ${desktopSidebarWidth ? `calc(100% - ${desktopSidebarWidth}px)` : '100%'};
          position: fixed;
          right: 0;
          bottom: 0;
          text-align: center;
          z-index: 4;
        }
        .content {
          display: inline-flex;
          align-items: center;
          background-color: ${backgroundColor};
          border-radius: 5px;
          padding: ${theme.spacing[2]} ${theme.spacing[3]};
          white-space: nowrap;
        }
        .icon {
          margin-right: ${theme.spacing[2]};
        }
        .button-wrapper {
          padding: ${theme.spacing[1.5]} ${theme.spacing[2.5]};
          border-radius: 5px;
          margin: -${theme.spacing[1.5]} -14px -${theme.spacing[1.5]} ${theme.spacing[2]};
          transition: background-color ${HOVER_TRANSITION}ms ease-in-out;
        }
        .button-wrapper:hover {
          background-color: rgba(255, 255, 255, 0.25);
        }
        @media ${MOBILE_SCREENSIZE} {
          .snackbar {
            width: 100%;
            bottom: ${mobileMenuHeight}px;
          }
          .content {
            white-space: inherit;
          }
        }
      `}</style>
    </>
  )
}
